import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from './lib/invalidate-loan-activity';
import { api } from '@/lib/api';
import { resolveActiveTenantId } from '@/lib/tenant-context';
import { useAuthStore } from '@/stores/auth-store';
import type { EmailConfig, EmailThread } from '@/types';

function parseJsonArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

function normalizeThread(thread: any): EmailThread {
  return {
    ...thread,
    messageCount: thread.messageCount ?? thread._count?.messages ?? 0,
    participants: parseJsonArray(thread.participants),
    messages: Array.isArray(thread.messages)
      ? thread.messages.map((message: any) => ({
          ...message,
          toAddresses: parseJsonArray(message.toAddresses),
          ccAddresses: parseJsonArray(message.ccAddresses),
          attachments: parseJsonArray(message.attachments),
        }))
      : [],
  };
}

function useActiveTenantId() {
  return useAuthStore(state => resolveActiveTenantId(state.user));
}

export function useEmailInbox(filters?: Record<string, any>) {
  const activeTenantId = useActiveTenantId();

  return useQuery<EmailThread[]>({
    queryKey: ['email-inbox', activeTenantId, filters],
    queryFn: async () => {
      const params = new URLSearchParams(filters || {});
      const res = await api.get(`/email/inbox?${params.toString()}`);
      return Array.isArray(res.data) ? res.data.map(normalizeThread) : [];
    },
    enabled: !!activeTenantId,
  });
}

export function useEmailThread(threadId: string) {
  const activeTenantId = useActiveTenantId();

  return useQuery<EmailThread>({
    queryKey: ['email-thread', activeTenantId, threadId],
    queryFn: async () => {
      const res = await api.get(`/email/threads/${threadId}`);
      return normalizeThread(res.data);
    },
    enabled: !!threadId && !!activeTenantId,
    retry: false,
  });
}

export function useEmailsByLoan(loanId: string) {
  const activeTenantId = useActiveTenantId();

  return useQuery<EmailThread[]>({
    queryKey: ['email-loan', activeTenantId, loanId],
    queryFn: async () => {
      const res = await api.get(`/email/loan/${loanId}`);
      return Array.isArray(res.data) ? res.data.map(normalizeThread) : [];
    },
    enabled: !!loanId && !!activeTenantId,
  });
}

export function useSendEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      to: string[];
      cc?: string[];
      subject: string;
      body: string;
      bodyHtml?: string;
      loanId?: string;
    }) => {
      const res = await api.post('/email/send', data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      const activeTenantId = resolveActiveTenantId(useAuthStore.getState().user);
      queryClient.invalidateQueries({ queryKey: ['email-inbox', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-thread', activeTenantId] });
      if (variables.loanId) {
        queryClient.invalidateQueries({
          queryKey: ['email-loan', activeTenantId, variables.loanId],
        });
        invalidateLoanActivity(queryClient, variables.loanId);
      } else {
        queryClient.invalidateQueries({ queryKey: ['email-loan', activeTenantId] });
      }
    },
  });
}

export function useMarkEmailRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      const res = await api.post(`/email/messages/${messageId}/read`);
      return res.data;
    },
    onSuccess: () => {
      const activeTenantId = resolveActiveTenantId(useAuthStore.getState().user);
      queryClient.invalidateQueries({ queryKey: ['email-inbox', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-thread', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-loan', activeTenantId] });
    },
  });
}

export function useStarEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, starred }: { messageId: string; starred: boolean }) => {
      const res = await api.post(`/email/messages/${messageId}/star`, { starred });
      return res.data;
    },
    onSuccess: () => {
      const activeTenantId = resolveActiveTenantId(useAuthStore.getState().user);
      queryClient.invalidateQueries({ queryKey: ['email-inbox', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-thread', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-loan', activeTenantId] });
    },
  });
}

export function useArchiveThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => {
      const res = await api.post(`/email/threads/${threadId}/archive`);
      return res.data;
    },
    onSuccess: () => {
      const activeTenantId = resolveActiveTenantId(useAuthStore.getState().user);
      queryClient.invalidateQueries({ queryKey: ['email-inbox', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-thread', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['email-loan', activeTenantId] });
    },
  });
}

export function useEmailConfig() {
  return useQuery<EmailConfig>({
    queryKey: ['email-config'],
    queryFn: async () => {
      const res = await api.get('/email/config');
      return res.data;
    },
  });
}

export function useSaveEmailConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config: Omit<EmailConfig, 'id'>) => {
      const res = await api.post('/email/config', config);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-config'] });
    },
  });
}
